import { SubscriptionFilters } from '../components/SubscriptionEditorFields';

export interface ValidationIssue {
    field: keyof SubscriptionFilters;
    type: 'error' | 'warning';
    message: string;
}

/**
 * Validates the current filter state.
 */
export function validateFilters(
    filters: SubscriptionFilters,
    options: { isBaseRentActive: boolean }
): ValidationIssue[] {
    const issues: ValidationIssue[] = [];

    const checkMinMax = (minField: keyof SubscriptionFilters, maxField: keyof SubscriptionFilters, label: string) => {
        const minVal = parseFloat(filters[minField] as string);
        const maxVal = parseFloat(filters[maxField] as string);

        if (!isNaN(minVal) && !isNaN(maxVal) && minVal > maxVal) {
            issues.push({
                field: minField,
                type: 'error',
                message: `${label}: Minimum darf nicht größer als Maximum sein.`
            });
        }
    };

    const checkPlausibility = (field: keyof SubscriptionFilters) => {
        const val = parseFloat(filters[field] as string);
        if (!isNaN(val) && val > 0 && val < 50) {
            issues.push({
                field,
                type: 'warning',
                message: 'Bist du sicher?'
            });
        }
    };

    // Rent checks
    checkMinMax('minRent', 'maxRent', 'Gesamtmiete');
    checkPlausibility('minRent');
    checkPlausibility('maxRent');

    if (options.isBaseRentActive) {
        checkMinMax('minBaseRent', 'maxBaseRent', 'Kaltmiete');
        checkPlausibility('minBaseRent');
        checkPlausibility('maxBaseRent');

        // Relationship checks
        const maxRent = parseFloat(filters.maxRent);
        const maxBaseRent = parseFloat(filters.maxBaseRent);
        if (!isNaN(maxRent) && !isNaN(maxBaseRent) && maxBaseRent > maxRent) {
            issues.push({
                field: 'maxBaseRent',
                type: 'error',
                message: 'Kaltmiete kann nicht höher als Gesamtmiete sein.'
            });
        }
    }

    // Rooms
    checkMinMax('minRooms', 'maxRooms', 'Zimmer');

    // Space
    checkMinMax('minSpace', 'maxSpace', 'Fläche');

    return issues;
}

/**
 * Calculates dependencies for slider/input changes to maintain sanity.
 */
export function getCouplingUpdates(
    field: string,
    value: string,
    filters: SubscriptionFilters
): Partial<SubscriptionFilters> {
    const updates: Partial<SubscriptionFilters> = { [field]: value };
    const numValue = value === '' ? NaN : parseFloat(value);

    if (isNaN(numValue)) return updates;

    // 1. Min/Max coupling (Sliders usually want to "push" the other boundary if crossed)
    if (field === 'maxRent') {
        const minRent = parseFloat(filters.minRent);
        if (!isNaN(minRent) && numValue < minRent) {
            updates.minRent = value;
        }
        // Also ensure MaxBaseRent <= MaxRent
        const maxBase = parseFloat(filters.maxBaseRent);
        if (!isNaN(maxBase) && maxBase > numValue) {
            updates.maxBaseRent = value;
        }
    }

    if (field === 'minRent') {
        const maxRent = parseFloat(filters.maxRent);
        if (!isNaN(maxRent) && numValue > maxRent) {
            updates.maxRent = value;
        }
    }

    if (field === 'maxBaseRent') {
        const minBase = parseFloat(filters.minBaseRent);
        if (!isNaN(minBase) && numValue < minBase) {
            updates.minBaseRent = value;
        }
        // Ensure MaxBaseRent <= MaxRent
        const maxRent = parseFloat(filters.maxRent);
        if (!isNaN(maxRent) && numValue > maxRent) {
            updates.maxRent = value;
        }
    }

    if (field === 'minBaseRent') {
        const maxBase = parseFloat(filters.maxBaseRent);
        if (!isNaN(maxBase) && numValue > maxBase) {
            updates.maxBaseRent = value;
        }
        // Warmmiete can never be below Kaltmiete: lift maxRent to at least minBase.
        const maxRent = parseFloat(filters.maxRent);
        if (!isNaN(maxRent) && numValue > maxRent) {
            updates.maxRent = value;
        }
    }

    return updates;
}

/**
 * Standardizes the transformation of string-based form filters 
 * into correctly typed null/numeric values for the API payload.
 */
export function prepareSubscriptionFilters(
    filters: SubscriptionFilters,
    options: { isBaseRentActive: boolean }
) {
    return {
        minRent: filters.minRent ? parseInt(filters.minRent) : null,
        maxRent: filters.maxRent ? parseInt(filters.maxRent) : null,
        minBaseRent: (options.isBaseRentActive && filters.minBaseRent) ? parseInt(filters.minBaseRent) : null,
        maxBaseRent: (options.isBaseRentActive && filters.maxBaseRent) ? parseInt(filters.maxBaseRent) : null,
        includeUnknowns: filters.includeUnknowns ?? true,
        minRooms: filters.minRooms ? parseFloat(filters.minRooms) : null,
        maxRooms: filters.maxRooms ? parseFloat(filters.maxRooms) : null,
        minSpace: filters.minSpace ? parseInt(filters.minSpace) : null,
        maxSpace: filters.maxSpace ? parseInt(filters.maxSpace) : null,
        wbsFilter: filters.wbsFilter || 'ANY',
        wgFilter: filters.wgFilter || 'ANY',
        swapFilter: filters.swapFilter || 'ANY',
        tempFilter: filters.tempFilter || 'ANY',
        providerFilter: filters.providerFilter || 'ANY',
        furnishedFilter: filters.furnishedFilter || 'ANY',
        closedAreaFilter: filters.closedAreaFilter || 'NONE',
    };
}
